import { msg, str } from '@lit/localize';
import get from 'lodash/get';
import type {
  TaskInteractionAction,
  TaskInteractionControl,
  TaskInteractionResponse,
} from '../../../api/types';
import { formatDateTime } from '../../../utils/format';
import type { WorkflowUserInputRequest } from '../dialogs/types.js';
import { sanitizeForDisplay } from '../utils/binary-display.js';

export type WorkflowUserInputOption = {
  value: string;
  itemText: string;
};

export type WorkflowUserInputControl =
  | {
      type: 'select';
      controlId: string;
      label: string;
      value: string;
      defaultValue?: string;
      options: WorkflowUserInputOption[];
    }
  | {
      type: 'file';
      controlId: string;
      label: string;
      accept?: string;
    }
  | {
      type: 'preview';
      value: unknown;
      emptyText?: string;
    };

export type WorkflowUserInputControls = {
  controls: WorkflowUserInputControl[];
  buttons: readonly WorkflowUserInputButton[];
};

export type WorkflowUserInputButton = {
  name: string;
  label: string;
  variant?: TaskInteractionAction['variant'];
};

export type WorkflowUserInputSubmission = {
  response: TaskInteractionResponse;
  file?: File;
};

type QueryRoot = ParentNode;

type ComboBoxValueElement = HTMLElement & {
  itemValue?: string | number;
};

function resolveComboValue(
  value: string | number | undefined,
  fallback?: string | number
): string | number | undefined {
  if (typeof value === 'string') {
    return value.trim() ? value : fallback;
  }
  return value ?? fallback;
}

function formatDisplayValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null ||
    value === undefined
  ) {
    return String(value ?? '');
  }
  try {
    return JSON.stringify(sanitizeForDisplay(value));
  } catch {
    return String(value);
  }
}

function formatDescriptionValue(
  value: unknown,
  format?: 'datetime'
): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (format === 'datetime') {
    const date = new Date(String(value));
    if (!Number.isNaN(date.getTime())) {
      return msg(str`expires ${formatDateTime(date)}`);
    }
  }
  return String(value);
}

function resolveOptions(
  control: Extract<TaskInteractionControl, { type: 'select' }>,
  request: WorkflowUserInputRequest
): WorkflowUserInputOption[] {
  const payload = request.input?.payload;
  const config = request.config ?? {};
  const source = control.source;

  if (source.type === 'payload-array') {
    if (!Array.isArray(payload)) {
      return [];
    }

    return payload.map((item, index) => {
      const rawValue =
        source.value === 'index'
          ? index
          : get(item as Record<string, unknown>, source.value.key);

      let itemText: string;
      const selectorKey = source.labelSelectorConfigKey;
      const selector =
        selectorKey &&
        typeof config[selectorKey] === 'string' &&
        config[selectorKey]?.trim()
          ? String(config[selectorKey]).trim()
          : undefined;

      if (selector && item && typeof item === 'object') {
        const selected = get(item as Record<string, unknown>, selector);
        itemText =
          selected !== undefined && selected !== null
            ? String(selected)
            : formatDisplayValue(item);
      } else if (source.label === 'value') {
        itemText = formatDisplayValue(item);
      } else {
        itemText = formatDisplayValue(
          get(item as Record<string, unknown>, source.label.key)
        );
      }

      return {
        value: String(rawValue ?? ''),
        itemText,
      };
    });
  }

  if (source.type !== 'payload-path-array') {
    return [];
  }

  const items = get(
    payload as Record<string, unknown> | undefined,
    source.path
  );
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => {
    const value = get(item as Record<string, unknown>, source.value.key);
    const label = get(item as Record<string, unknown>, source.label.key);
    const description = source.descriptionKey
      ? formatDescriptionValue(
          get(item as Record<string, unknown>, source.descriptionKey),
          source.descriptionFormat
        )
      : undefined;

    return {
      value: String(value ?? ''),
      itemText: description
        ? `${String(label ?? '')} (${description})`
        : String(label ?? ''),
    };
  });
}

function resolveControl(
  control: TaskInteractionControl,
  request: WorkflowUserInputRequest
): WorkflowUserInputControl {
  if (control.type === 'preview') {
    return {
      type: 'preview',
      value: sanitizeForDisplay(request.input?.payload),
      emptyText: control.emptyText,
    };
  }

  if (control.type === 'file') {
    return {
      type: 'file',
      controlId: control.controlId,
      label: control.label,
      accept: control.accept,
    };
  }

  const options = resolveOptions(control, request);
  return {
    type: 'select',
    controlId: control.controlId,
    label: control.label,
    value: options[0]?.itemText ?? '',
    defaultValue:
      control.defaultValue === 'first' ? options[0]?.value : undefined,
    options,
  };
}

export function getWorkflowUserInputControls(
  request: WorkflowUserInputRequest
): WorkflowUserInputControls {
  const controls = (request.interaction?.controls ?? []).map((control) =>
    resolveControl(control, request)
  );
  const buttons =
    request.interaction?.actions?.length &&
    request.interaction.actions.length > 0
      ? request.interaction.actions
      : ([{ name: 'confirm', label: 'Confirm', variant: 'primary' }] as const);

  return {
    controls,
    buttons,
  };
}

export function buildWorkflowUserInputSubmission(args: {
  request: WorkflowUserInputRequest;
  renderRoot: QueryRoot;
  actionName: string;
  controlIdPrefix?: string;
}): WorkflowUserInputSubmission | undefined {
  const { request, renderRoot, actionName, controlIdPrefix = '' } = args;
  const { controls } = getWorkflowUserInputControls(request);
  const response: TaskInteractionResponse = { action: actionName };
  let file: File | undefined;
  let hasActionableValue = false;

  if (actionName === 'abort') {
    return { response };
  }

  for (const control of controls) {
    if (control.type === 'preview') {
      continue;
    }

    if (control.type === 'file') {
      const input = renderRoot.querySelector(
        `input[data-control-id="${controlIdPrefix}${control.controlId}"]`
      ) as HTMLInputElement | null;
      const selectedFile = input?.files?.[0];
      if (selectedFile) {
        file = selectedFile;
        hasActionableValue = true;
      }
      continue;
    }

    const combo = renderRoot.querySelector(
      `sp-combobox[data-control-id="${controlIdPrefix}${control.controlId}"]`
    ) as ComboBoxValueElement | null;
    const value = resolveComboValue(
      combo?.itemValue,
      control.defaultValue ?? control.options[0]?.value
    );

    if (value !== undefined && value !== '') {
      response[control.controlId] = String(value);
      hasActionableValue = true;
    }
  }

  if (
    actionName === 'confirm' &&
    !hasActionableValue &&
    controls.some(
      (control) => control.type === 'select' || control.type === 'file'
    )
  ) {
    return undefined;
  }

  return { response, file };
}
